/**
 * Telling the people who make this thing that it is wrong.
 *
 * ## Why this is part of the database rather than a support link
 *
 * A botanical catalog is never finished and never entirely right. The person best placed to notice
 * that *Amelanchier* fruits three weeks earlier than the record claims, or that a shrub is listed at
 * half its real spread, is the designer who has just laid forty of them out and knows the plant.
 * That knowledge reaches nobody if reporting it means leaving the app, finding an address, and
 * describing which of four hundred figures is wrong in prose.
 *
 * So a suggestion is raised **from the thing being suggested about**, and arrives already carrying
 * what it is about: the catalog id, the field path, the value on record, and the value being
 * proposed. The person sending it types one sentence; everything else is filled in from where they
 * pressed.
 *
 * ## Four levels, one shape
 *
 * - **Parameter** — "this figure is wrong", raised off a table cell or a compare row. Carries
 *   `floraId`, `field`, `currentValue`.
 * - **Record** — "this plant needs work", raised off a card or a gallery plate. Carries `floraId`.
 * - **Catalog** — "this plant is missing", or a note about a whole class of plants. Carries neither.
 * - **App** — "this tool is wrong", raised from the toolbar. The one level that is not about a plant
 *   at all, so in place of a catalog id it carries {@link FeedbackDraft.context}: what the app knew
 *   about the session when the button was pressed.
 *
 * They are one {@link FeedbackDraft} with optional fields rather than four types, because the form,
 * the queue and the transport are identical and the only thing that varies is how much context was
 * available at the press. The app level is the reason `floraId` was optional in the first place.
 *
 * ## Penciled in, deliberately
 *
 * {@link FeedbackChannel} is the seam. The app ships with {@link localFeedbackChannel}, which queues
 * to this browser and puts a formatted report on the clipboard — enough to be genuinely usable
 * today, and honest about the fact that nothing has been sent anywhere. Pointing this at a real
 * endpoint is one implementation of a one-method interface, and no calling code changes.
 */

export type FeedbackScope = 'parameter' | 'record' | 'catalog' | 'app';

/**
 * What kind of thing is being said.
 *
 * Asked for at the app level and nowhere else: a suggestion raised off a table cell is already known
 * to be a correction, whereas "feedback" with no qualifier could be a crash report or a thank-you,
 * and those get read by different people in a different order. Three options rather than a taxonomy —
 * the sentence underneath does the real work, and a long menu in front of a text box is a toll gate.
 */
export type FeedbackTopic = 'problem' | 'idea' | 'praise';

export const FEEDBACK_TOPICS: readonly { readonly id: FeedbackTopic; readonly label: string }[] = [
  { id: 'problem', label: 'Something is wrong' },
  { id: 'idea', label: 'I have an idea' },
  { id: 'praise', label: 'This worked well' },
];

/** How a topic is written into a report. */
export function topicLabel(topic: FeedbackTopic): string {
  return FEEDBACK_TOPICS.find((entry) => entry.id === topic)?.label ?? topic;
}

export interface FeedbackDraft {
  scope: FeedbackScope;
  /** Fault, idea, or praise. Asked at app scope; the narrower scopes are corrections by definition. */
  topic?: FeedbackTopic;
  /**
   * What the app knew when the button was pressed — one short fact per line, in the wording the
   * sender is shown before they send. A bug report that names the site, the plant count and the
   * browser is worth several rounds of correspondence, and the honest way to collect that is to
   * gather it openly rather than to attach it quietly.
   */
  context?: readonly string[];
  /** The catalog record this is about. Absent on a catalog-level suggestion. */
  floraId?: string;
  /** Human name for the record, so a queued report reads without a catalog lookup. */
  floraName?: string;
  /** Dotted path into the record — `growth.matureHeightMeters`. Parameter scope only. */
  field?: string;
  /** Label the field is shown under, which is what the sender actually saw. */
  fieldLabel?: string;
  /** The value on record right now, formatted as it was displayed. */
  currentValue?: string;
  /** What the sender thinks it should be. Free text — a suggestion is not a schema edit. */
  proposedValue?: string;
  /** Why. The one field that is always asked for. */
  message: string;
  /** Where the sender can be reached, when they choose to say. */
  contact?: string;
}

export interface FeedbackRecord extends FeedbackDraft {
  id: string;
  submittedAt: number;
  /** Catalog build the suggestion was raised against — a report about a stale build is noise. */
  catalogVersion: string;
  /** App build, for the same reason. */
  appVersion: string;
}

export interface FeedbackResult {
  ok: boolean;
  /** One line to show the sender. Says what actually happened, including when nothing was sent. */
  message: string;
}

export interface FeedbackChannel {
  /** How the channel describes itself to the sender, above the send button. */
  readonly describe: string;
  submit: (record: FeedbackRecord) => Promise<FeedbackResult>;
}

/** Where the local queue lives. Shared with nothing else. */
const QUEUE_KEY = 'lushglade.feedback.queue';
const QUEUE_LIMIT = 200;

/** A suggestion as plain text — what goes on the clipboard, and what a real endpoint would carry. */
export function formatFeedback(record: FeedbackRecord): string {
  const kind = record.scope === 'app' ? 'feedback' : 'catalog suggestion';
  const lines = [
    `Lush Glade — ${kind} (${record.scope})`,
    `Raised: ${new Date(record.submittedAt).toISOString()}`,
    `Catalog: ${record.catalogVersion}   App: ${record.appVersion}`,
  ];
  if (record.topic) lines.push(`Topic: ${topicLabel(record.topic)}`);
  if (record.floraId) lines.push(`Plant: ${record.floraName ?? record.floraId} [${record.floraId}]`);
  if (record.field) lines.push(`Field: ${record.fieldLabel ?? record.field} (${record.field})`);
  if (record.currentValue !== undefined) lines.push(`On record: ${record.currentValue}`);
  if (record.proposedValue) lines.push(`Proposed: ${record.proposedValue}`);
  if (record.contact) lines.push(`Contact: ${record.contact}`);
  lines.push('', record.message);
  // Last, and clearly separated: it is evidence, and it must not be the first thing read. The person
  // reading a queue of these wants the sentence somebody typed before the machine's account of itself.
  if (record.context && record.context.length > 0) {
    lines.push('', 'What the app knew:', ...record.context.map((line) => `  ${line}`));
  }
  return lines.join('\n');
}

/** Everything queued in this browser, oldest first. Exposed so an admin view can drain it later. */
export function readFeedbackQueue(): FeedbackRecord[] {
  try {
    const raw = globalThis.localStorage?.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as FeedbackRecord[]) : [];
  } catch {
    // A corrupt queue is not worth failing a suggestion over — it is replaced on the next write.
    return [];
  }
}

export function clearFeedbackQueue(): void {
  try {
    globalThis.localStorage?.removeItem(QUEUE_KEY);
  } catch {
    /* Storage is unavailable; the queue was never written either. */
  }
}

/**
 * The shipped channel: queue it here, and put it on the clipboard so it can be sent by hand.
 *
 * It reports what it did rather than claiming success it has not earned — "saved in this browser and
 * copied to your clipboard" is true, and "thanks, we've received that" would not be. When the
 * clipboard is unavailable the suggestion is still queued, and the message says so.
 */
export function localFeedbackChannel(): FeedbackChannel {
  return {
    describe:
      'Suggestions are held in this browser and copied to your clipboard — nothing is sent anywhere yet.',
    submit: async (record) => {
      const queue = readFeedbackQueue();
      queue.push(record);
      // Tested for rather than optional-chained, for the same reason as the clipboard below: an
      // optional call on a missing API succeeds silently, and "held in this browser" would then be
      // reported about a suggestion that went nowhere.
      const storage = globalThis.localStorage;
      let stored = false;
      if (typeof storage?.setItem === 'function') {
        try {
          storage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-QUEUE_LIMIT)));
          stored = true;
        } catch {
          stored = false;
        }
      }

      // Tested for rather than optional-chained. `navigator.clipboard?.writeText(…)` on a browser
      // without the API evaluates to `undefined`, which awaits happily — and the channel would then
      // report a copy it never made, which is precisely the kind of comfortable lie this file exists
      // to avoid.
      const clipboard = globalThis.navigator?.clipboard;
      let copied = false;
      if (typeof clipboard?.writeText === 'function') {
        try {
          await clipboard.writeText(formatFeedback(record));
          copied = true;
        } catch {
          copied = false;
        }
      }

      if (copied && stored) {
        return { ok: true, message: 'Copied to your clipboard and held in this browser.' };
      }
      if (copied) return { ok: true, message: 'Copied to your clipboard.' };
      if (stored) {
        return { ok: true, message: 'Held in this browser — the clipboard was not available.' };
      }
      return {
        ok: false,
        message: 'Could not save or copy the suggestion. Storage and the clipboard both refused.',
      };
    },
  };
}

export interface FeedbackSubmitOptions {
  catalogVersion: string;
  appVersion: string;
}

/** Stamp a draft and hand it to the channel. */
export async function submitFeedback(
  channel: FeedbackChannel,
  draft: FeedbackDraft,
  options: FeedbackSubmitOptions,
): Promise<FeedbackResult> {
  const record: FeedbackRecord = {
    ...draft,
    id: `fb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    submittedAt: Date.now(),
    catalogVersion: options.catalogVersion,
    appVersion: options.appVersion,
  };
  return channel.submit(record);
}

/** The heading a suggestion form opens under, which is how the sender knows what they are editing. */
export function feedbackTitle(draft: FeedbackDraft): string {
  switch (draft.scope) {
    case 'parameter':
      return `Suggest a value — ${draft.fieldLabel ?? draft.field ?? 'parameter'}`;
    case 'record':
      return `Suggest a correction — ${draft.floraName ?? 'this plant'}`;
    case 'catalog':
      return 'Suggest a plant for the catalog';
    case 'app':
      return 'Leave feedback';
  }
}

/** The placeholder in the message box, which is where a form teaches what a good report looks like. */
export function feedbackPrompt(draft: FeedbackDraft): string {
  switch (draft.scope) {
    case 'parameter':
      return 'What should this be, and how do you know? A source or a region helps.';
    case 'record':
      return 'What is wrong or missing on this plant?';
    case 'catalog':
      return 'Which plant, and what would you use it for?';
    // The one scope whose question depends on the answer above it: "what went wrong" and "what would
    // you like" are different invitations, and asking the wrong one gets a shorter reply.
    case 'app':
      switch (draft.topic) {
        case 'problem':
          return 'What went wrong, and what were you doing when it happened?';
        case 'idea':
          return 'What would you like Lush Glade to do?';
        case 'praise':
          return 'What worked? Knowing what not to break is worth as much as a bug report.';
        default:
          return 'What is on your mind?';
      }
  }
}
